import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Quantity } from '~/graphql/graphql';
import {
  bundleCountForProductLength,
  type BundleProductLengthLike,
} from '~/components/ui/bundleQuantity';
import { formatQuantityCell } from '~/utils';

type BundleQuantityLabelProps = {
  quantity: Quantity;
  piecesPerBundle: number;
  platformProductsLength: BundleProductLengthLike;
  formatLeadLabel?: (label: string) => string;
  actions?: ReactNode;
  align?: 'start' | 'end';
  className?: string;
};

export function BundleQuantityLabel({
  quantity,
  piecesPerBundle,
  platformProductsLength,
  formatLeadLabel = (label) => label,
  actions,
  align = 'end',
  className = '',
}: Readonly<BundleQuantityLabelProps>) {
  const { t } = useTranslation('general');
  const bundleCount = bundleCountForProductLength(
    quantity,
    piecesPerBundle,
    platformProductsLength
  );
  const unitLabel = formatQuantityCell(quantity.value, quantity.unit);
  const columnAlign = align === 'start' ? 'items-start' : 'items-end';
  const rowJustify = align === 'start' ? 'justify-start' : 'justify-end';

  return (
    <span className={`flex flex-col ${columnAlign} tabular-nums ${className}`}>
      <span className={`flex items-center ${rowJustify} gap-1.5`}>
        {bundleCount === null ? (
          <span data-testid="bundle-quantity-unit">
            {formatLeadLabel(unitLabel)}
          </span>
        ) : (
          <span data-testid="bundle-quantity-bundles">
            {formatLeadLabel(t('bundle_count', { count: bundleCount }))}
          </span>
        )}
        {actions}
      </span>
      {bundleCount !== null && (
        <span
          className="text-xs font-normal text-neut-sec"
          data-testid="bundle-quantity-unit"
        >
          {unitLabel}
        </span>
      )}
    </span>
  );
}
